import { useMemo, useRef } from 'react';
import { useAuth } from '../../utils/Auth';
import { useOrganization } from '../../utils/OrganizationContext';
import { useNavigate, useLocation } from 'react-router-dom';
import LogoDark from '../Images/LiquidQR-dark.png';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from './variants';
import { useClickOutside } from '../../utils/hooks/useClickOutside';
import { useNavigation } from '../../utils/NavigationContext';
import OrganizationSelector from '../OrganizationSelector';
import styles from './MobileNavigation.module.css';

export default function MobileNavigation() {
    const { currentUser, logout, orgObj, isLoading } = useAuth();
    const { orgMetadata } = useOrganization();
    const navigate = useNavigate();
    const location = useLocation();

    const moreBtnRef = useRef(null);
    const [ moreRef, showMore, setShowMore ] = useClickOutside(false, moreBtnRef);

    const { collectionMenuBtnRef, showCollectionMenu, setShowCollectionMenu } = useNavigation();

    const linePosition = useMemo(() => {
        if (isLoading) return 80;
        switch (location.pathname) {
            case '/':
            case '/home':
                return 0;
            case '/collection':
            case '/collection/edit':
                return 20;
            case '/collection/new':
                return 40;
            case '/login':
            case '/signup':
            default:
                return 80;
        };
    },[location?.pathname, isLoading]);

    const isPersonalOrg = orgObj?.metadata?.type === 'personal';
    const isAccountExpired = !isPersonalOrg && orgMetadata?.active === false;

    const handleNavigate = (location) => {
        setShowMore(false);
        switch (location) {
            case 'home':
                return navigate('/');
            case 'create':
                if (isAccountExpired) return;
                return navigate('/collection/new');
            case 'store':
                return window.open(`https://liquidqr.com/store`, '_blank');
            case 'collection':
            case 'settings':
            case 'analytics':
            case 'organizations/settings':
                return navigate(`/${location}`);
            case 'login':
            case 'signup':
                return navigate(`/auth?mode=${location}`)
            default:
                break;
        };
    };

    const handleLogout = e => {
        e.preventDefault();
        setShowMore(false);
        setShowCollectionMenu(false);
        logout();
    };

    const handleReturn = useMemo(() => {
        const isCollection = location?.pathname === '/collection'
        if (currentUser) {
            return (
                <>
                <button type='button' className={styles.navBtn} onClick={() => handleNavigate('home')}>
                    <span className={styles.icon}><i className='fas fa-home' /></span>
                    <span className={styles.word}>Home</span>
                </button>
                <button type='button' className={styles.navBtn} onClick={() => handleNavigate('collection')}>
                    <span className={styles.icon}><i className='fak fa-liquidqr' /></span>
                    <span className={styles.word}>QR Codes</span>
                </button>
                <button
                    type='button'
                    className={styles.navBtn}
                    id='create'
                    onClick={() => handleNavigate('create')}
                    disabled={isAccountExpired}
                    title={isAccountExpired ? 'Account expired. Visit Organization Settings to reup your subscription.' : undefined}
                >
                    <span className={styles.circle}><i className='fas fa-circle-plus' /></span>
                    <span className={styles.icon}></span>
                    <span className={styles.word}>Create</span>
                </button>
                {isCollection ? (
                    <button type='button' className={styles.navBtn} onClick={() => setShowCollectionMenu(!showCollectionMenu)} ref={collectionMenuBtnRef}>
                        <span className={styles.icon}><i className={showCollectionMenu ? 'far fa-arrow-down-to-line' : 'far fa-arrow-up-from-line'} /></span>
                        <span className={styles.word}>QR Menu</span>
                    </button>
                ) : (
                    <button type='button' className={styles.navBtn} onClick={() => handleNavigate('store')}>
                        <span className={styles.icon}><i className='fas fa-store' /></span>
                        <span className={styles.word}>Shop QRs</span>
                    </button>
                )}
                </>
            )
        } else {
            return (
                <>
                <button type='button' disabled className={styles.navBtn} />
                <button type='button' disabled className={styles.navBtn} />
                <button type='button' disabled className={styles.navBtn} />
                <button type='button' disabled className={styles.navBtn} />
                </>
            );
        };
    },[currentUser, location?.pathname, isLoading, showCollectionMenu]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <div className={styles.underline} style={{left: `${linePosition}%`}}/>
                {handleReturn}
                <button type='button' className={styles.navBtn} ref={moreBtnRef} onClick={() => setShowMore(!showMore)}>
                    <span className={styles.icon}><i className='fas fa-bars' /></span>
                    <span className={styles.word}>More</span>
                </button>
            </div>
            <AnimatePresence initial={false}>
                {showMore && (
                    <motion.div 
                        className={styles.menu}
                        transition={transition}
                        variants={variants}
                        initial='hideMenu'
                        animate='showMenu'
                        exit='hideMenu'
                        ref={moreRef}
                        style={{padding: `${currentUser ? '.25rem 0 1.5rem' : '.25rem 0 .75rem'}`}}
                    >
                        <div className={styles.logo} style={{marginBottom: `${currentUser ? '.5rem' : '.25rem'}`}}>
                            <img src={LogoDark} />
                        </div>
                        {currentUser ? 
                            <>
                            <div className={styles.menuTitle}><OrganizationSelector /></div>
                            <button type='button' className={`${styles.menuItem} ${location?.pathname === '/organizations/settings' ? styles.active : ''}`} onClick={() => handleNavigate('organizations/settings')} ><i className='fas fa-users' /> Organization Settings</button>
                            <button type='button' className={styles.menuItem} onClick={() => handleNavigate('store')} style={{display: `${location?.pathname === '/collection' ? 'flex' : 'none'}`}}><i className='fas fa-store' />Shop QRs</button>
                            <button type='button' className={`${styles.menuItem} ${location?.pathname === '/settings' ? styles.active : null}`} onClick={() => handleNavigate('settings')}><i className='fas fa-gear' />Account Settings</button>
                            <button type='button' className={location?.pathname === '/analytics' ? `${styles.menuItem} ${styles.active}` : styles.menuItem} onClick={() => handleNavigate('analytics')} style={{display: `${(orgObj?.plan?.analytics > 1 && orgObj?.plan?.plan_id > 2) ? 'flex' : 'none'}`}}><i className='fas fa-chart-mixed' />Analytics</button>
                            <button type='button' className={styles.menuItem} onClick={handleLogout}><i className='fas fa-arrow-right-from-bracket' />Logout</button>
                            </>
                            :
                            <>
                            <button type='button' className={`${styles.menuItem} ${location?.pathname === '/login' ? styles.active : null}`} onClick={() => handleNavigate('login')}><i className='fas fa-arrow-right-to-bracket' />Log In</button>
                            <button type='button' className={`${styles.menuItem} ${location?.pathname === '/signup' ? styles.active : null}`} onClick={() => handleNavigate('signup')}><i className='fas fa-user-plus' />Sign Up</button>
                            </>
                        }
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};